import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { store } from "../../../../app/store";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// mui components
import { Box, Stack } from "@mui/material";

// api slice
import { useUpdateFieldValuesTpoMutation } from "../../../../features/update-field-value-tpo/updateFieldsApiSlice";
import { setLoanDocsReducer } from "../../../../features/loan-docs/loanDocsSlice";
import { useBrokerUploadsMutation } from "../../../../features/broker-uploads/brokerUploadsApiSlice";
import { getBrokerUploads } from "../../../../app/services/broker-upload";
import {
	getMimeTypeFromFilename,
	GENERIC_FALLBACK_CONTENT_TYPE,
} from "../../../../utils/document-validation";
import { useAttachEnhancedConditionDocumentMutation } from "../../../../features/attach-enhanced-condition-document/attach-enhanced-condition-document";
import { useEnhancedConditionsTemplatesMutation } from "../../../../features/enhanced-conditions-templates/fetchEnhancedConditionsTemplatesApiSlice";
import { useEnhancedConditionsTemplateDetailsMutation } from "../../../../features/enhanced-conditions-template-details/fetchEnhancedConditionsTemplateDetailsApiSlice";
import { useEnhancedConditionsEFolderCreationMutation } from "../../../../features/create-enhanced-conditions-efolder/createEnhancedConditionsEfolderApiSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionsUploadAttachmentUrlAndAssignEfolderMutation } from "../../../../features/fetch-enhanced-conditions-upload-attachment-url-and-assign-efolder/fetchEnhancedConditionsUploadAttachmentUrlAndAssignEfolderApiSlice";
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../../features/fetch-enhanced-conditions-efolder-documents-list/fetchEnhancedConditionsEfolderDocumentsApiSlice";
import { useAttachEnhancedConditionDocumentToUploadUrlMutation } from "../../../../features/attach-enhanced-condition-document-to-uploadurl/attach-enhanced-condition-document-to-uploadurl";
import { useEnhancedLoanUploadAttachmentForS3FallbackMutation } from "../../../../features/enhanced-loan-upload-attachment-for-s3-fallback/enhanced-loan-upload-attachment-for-s3-fallback";
import { useAttachDocumentConditionMutation } from "../../../../features/attach-document-condition/attach-document-condition";
import { useGetStandardDocumentUploadUrlMutation } from "../../../../features/get-standard-document-upload-url/get-standard-document-upload-url";
import { useStandardDocumentUploadFileForS3FallbackMutation } from "../../../../features/standard-document-upload-file-for-s3-fallback/standard-document-upload-file-for-s3-fallback";
import { getEnhancedConditionsDocumentsList } from "../../../../app/services/enhanced-conditions-documents-list";

// reducer slices
import {
	selectSelectedOrganization,
	selectOrganizationName,
	selectUserDetails,
} from "../../../../features/auth/authSlice";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";

// custom components
import Heading3 from "../../../typography/heading-03";
import SupportingDocs from "../../../supporting-docs";
import PrimaryButton from "../../../buttons/primary-button";
import SecondaryButton from "../../../buttons/secondary-button";
import Body2 from "../../../typography/body-02";
import Body3 from "../../../typography/body-03";
import CustomTable from "../../../custom-table";

// helper utils
import { formatBytes } from "../../../../utils/format-bytes";
import { fileToBase64 } from "../../../../utils/file-to-base64";
import { uploadFileWithChunking } from "../../../../utils/upload-file-with-chunks";
import { uploadMultipartFile } from "../../../../utils/upload-multipart-file";
import {
	runSupportingDocsEnhancedUpload,
	runSupportingDocsStandardUpload,
} from "../../../../utils/supporting-docs-submit";
import { waitForPendingApiRequestsToSettle } from "../../../../features/api-request-status/apiRequestStatusSlice";
import {
	isRetryResponse,
	isRetryItem,
	createRetryHandledError,
	isRetryHandledError,
} from "../../../../utils/document-retry-helper";

// custom styles
import styles from "./index.module.scss";

const LOAN_IN_USE_UPLOAD_MESSAGE =
	"The loan is currently in use. The document upload will be retried automatically after some time.";
const LOAN_IN_USE_UPLOAD_ERROR = {
	code: "LC-LOAN-IN-USE-409",
	title: "Loan currently in use",
	message: LOAN_IN_USE_UPLOAD_MESSAGE,
};

const LOAN_IN_USE_FALLBACK_MESSAGE = `Document Received. \n\n Your document has been saved, but the loan is currently in use by a Logan team member. LoganConnect will automatically add the document once the loan is available.\n\n No action is needed. Please do not upload the document again.\n\n To confirm when your document is available to Logan Operations, please check the document status on the Documents screen.`;

const isConflictError = (error) =>
	error?.status === 409 ||
	error?.originalStatus === 409 ||
	error?.OriginalStatus === 409 ||
	error?.status_code === 409;

const showLoanInUseError = (handleOpenCommonErrorModal, errorResponse) => {
	handleOpenCommonErrorModal?.({
		...LOAN_IN_USE_UPLOAD_ERROR,
		error_response: errorResponse ?? null,
	});
};

const DocumentsTrackerDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	setDocumentTrackerUploadProgress = () => {},
	handleOpenCommonErrorModal,
	drawerData,
	fieldData,
}) => {
	const fileTypes = [
		"PDF",
		"DOC",
		"DOCX",
		"TXT",
		"TIF",
		"JPG",
		"JPEG",
		"JPE",
		"EMF",
		"XPS",
	];
	const supportingDocsLog = (...args) => {
		console.log("[SupportingDocs]", ...args);
	};
	// Callers on routes without `:id` (e.g. AURA condition rows) pass the loan guid in drawerData.
	const routeParams = useParams();
	const params = drawerData?.loanGuid
		? { ...routeParams, id: drawerData.loanGuid }
		: routeParams;
	const dispatch = useDispatch();

	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);
	const userDetails = useSelector(selectUserDetails);
	const currentOrganization = selectedOrganization || organizationName;
	const currentLoggedInUserEmail = String(userDetails?.email ?? "").trim();
	const enhancedConditionsStatusFromStore = useSelector(
		selectEnhancedConditionStatus
	);

	const [updateFieldValuesTpo] = useUpdateFieldValuesTpoMutation();
	const [brokerUploads] = useBrokerUploadsMutation();
	const [attachEnhancedConditionDocument] =
		useAttachEnhancedConditionDocumentMutation();
	const [enhancedConditionsDocumentsList] =
		useEnhancedConditionsDocumentsListMutation();
	const [enhancedConditionsTemplates] =
		useEnhancedConditionsTemplatesMutation();
	const [enhancedConditionsTemplateDetails] =
		useEnhancedConditionsTemplateDetailsMutation();
	const [enhancedConditionsEFolderCreation] =
		useEnhancedConditionsEFolderCreationMutation();
	const [enhancedConditionsUploadUrlAndAssignEfolder] =
		useEnhancedConditionsUploadAttachmentUrlAndAssignEfolderMutation();
	const [attachEnhancedConditionDocumentToUploadUrl] =
		useAttachEnhancedConditionDocumentToUploadUrlMutation();
	const [enhancedLoanUploadAttachmentForS3Fallback] =
		useEnhancedLoanUploadAttachmentForS3FallbackMutation();
	const [attachDocumentCondition] = useAttachDocumentConditionMutation();
	const [getStandardDocumentUploadUrl] =
		useGetStandardDocumentUploadUrlMutation();
	const [standardDocumentUploadFileForS3Fallback] =
		useStandardDocumentUploadFileForS3FallbackMutation();
	const [getEnhancedConditionsEfolderDocumentsList] =
		useEnhancedConditionsEfolderDocumentsListMutation();

	const [attachedFiles, setAttachedFiles] = useState([]);
	const [selectedConditionTypes, setSelectedConditionTypes] = useState([]);
	const uploadSuccessModalShownRef = useRef(false);
	const loanInUseFallbackSuccessRef = useRef(false);
	const latestRetryContextRef = useRef({});
	// Tracks files that already succeeded via the primary (non-fallback) upload
	// path during the current submission, so a later file's 409 conflict
	// doesn't re-send those already-uploaded files through the S3 fallback too.
	const uploadedFilesThisSubmissionRef = useRef(new Set());
	const uploadHeaderText =
		drawerData?.uploadHeaderText ?? "Upload Documents tracker";
	const hideConditionSelection = Boolean(drawerData?.hideConditionSelection);
	const uploadConditionId = drawerData?.uploadConditionId;
	const getSelectedFile = (item) => item?.file ?? item;
	const getUploadTrackerKey = (uploadData, selectedFile) =>
		String(
			uploadData?.attachmentId ??
				uploadData?.file_unique_id ??
				`${selectedFile?.name ?? "file"}-${selectedFile?.size ?? 0}`
		);
	const getUploaderDisplayName = () =>
		userDetails?.email ??
		userDetails?.name ??
		userDetails?.display_name ??
		userDetails?.full_name ??
		"-";

	const rememberRetryContext = (context = {}) => {
		latestRetryContextRef.current = {
			...latestRetryContextRef.current,
			...context,
		};
	};

	const markFileUploaded = (item) => {
		const selectedFile = getSelectedFile(item);
		if (selectedFile) {
			uploadedFilesThisSubmissionRef.current.add(selectedFile);
		}
	};

	const buildMultipartUploadProgress = (selectedFile, uploadKey) => {
		const fileName = selectedFile?.name ?? "Unknown file";
		const uploadedAt = DateTime.now()
			.setZone("America/New_York")
			.toFormat("MMM d, yyyy, h:mm a");

		return {
			uploadKey,
			fileName,
			fileSize: selectedFile?.size ?? 0,
			createdDate: new Date().toISOString(),
			updatedDate: new Date().toISOString(),
			time: `${uploadedAt} ET`,
			uploadedBy: getUploaderDisplayName(),
			status: "Processing file in progress",
		};
	};

	const enhancedConditionsStatus = String(
		enhancedConditionsStatusFromStore ?? ""
	)
		.trim()
		.toUpperCase();
	const isEnhancedEnabled = enhancedConditionsStatus === "Y";
	const selectedLoanId = params?.id;

	const CHUNK_SIZE_BYTES = 59 * 1024 * 1024; // 59MB

	const appendChunkMetadata = (formData, meta) => {
		if (!meta?.isChunked) return;

		formData.append("chunk_index", String(meta?.chunkIndex));
		formData.append("total_chunks", String(meta?.totalChunks));
		formData.append("upload_id", meta?.uploadId);
		formData.append("original_file_name", meta?.originalFileName);
		formData.append("original_file_size", String(meta?.originalFileSize));
		formData.append("is_chunked", "true");
		formData.append("is_last_chunk", String(meta?.isLastChunk));
	};

	const extractFallbackS3Url = (response) => {
		const candidates = [
			response?.data?.s3_url,
			response?.data?.s3Url,
			response?.data?.uploadUrl,
			response?.data?.url,
			response?.s3_url,
			response?.s3Url,
			response?.uploadUrl,
			response?.url,
			response?.data?.[0]?.s3_url,
			response?.data?.[0]?.s3Url,
		];

		const resolvedS3Url = candidates.find((value) =>
			String(value ?? "").trim()
		);

		return String(resolvedS3Url ?? "").trim();
	};

	const getCurrentRetryFile = (retryContext = {}) =>
		getSelectedFile(
			retryContext?.file ??
				latestRetryContextRef.current?.file ??
				attachedFiles?.[0]
		);

	const getCurrentRetryFiles = (retryContext = {}) => {
		const explicitFiles =
			retryContext?.files ?? latestRetryContextRef.current?.files;

		// No explicit file list was handed to this retry (the common case) —
		// fall back to everything currently attached, minus any file that
		// already succeeded via the primary upload path this submission, so
		// a later file's conflict doesn't re-send already-uploaded files too.
		const rawFiles =
			Array.isArray(explicitFiles) && explicitFiles.length
				? explicitFiles
				: (attachedFiles ?? []).filter(
						(item) =>
							!uploadedFilesThisSubmissionRef.current.has(getSelectedFile(item))
					);

		return (Array.isArray(rawFiles) ? rawFiles : [rawFiles])
			.map((item) => getSelectedFile(item))
			.filter(Boolean);
	};

	async function retryConflictWithS3Fallback(retryContext = {}) {
		const resolvedLoanType = String(
			retryContext?.loanType ?? (isEnhancedEnabled ? "Enhanced" : "Standard")
		).trim();
		const isEnhancedLoan = resolvedLoanType.toLowerCase() === "enhanced";

		if (!isEnhancedLoan) {
			const selectedFiles = getCurrentRetryFiles(retryContext);

			if (!selectedFiles.length) {
				handleErrorModal(
					"DOCUMENT_UPLOAD_ERROR",
					new Error("Missing file for S3 fallback retry")
				);
				return null;
			}

			const resolvedConditionIds = Array.isArray(
				retryContext?.selectedConditionIds
			)
				? retryContext?.selectedConditionIds
				: Array.isArray(selectedConditionTypes)
					? selectedConditionTypes
					: [];

			let lastFallbackResponse = null;

			for (const file of selectedFiles) {
				let fallbackResponse = null;

				try {
					fallbackResponse = await uploadStandardFileToS3Fallback(
						file,
						resolvedConditionIds,
						resolvedLoanType || "Standard"
					);
				} catch (error) {
					supportingDocsLog(
						"[SupportingDocs] S3 fallback upload failed for file, continuing with remaining files",
						{ loanGuid: params?.id, fileName: file?.name, error }
					);
					continue;
				}

				const s3Url = extractFallbackS3Url(fallbackResponse);

				if (s3Url) {
					rememberRetryContext({
						...retryContext,
						file,
						originalFileName: file?.name,
						attachmentFilename: file?.name,
						attachmentSize: file?.size ?? "",
						contentType: file?.type ?? "",
						s3Url,
						fileContent: file?.content ?? "",
					});
				} else {
					supportingDocsLog(
						"[SupportingDocs] S3 fallback returned 200 without s3_url; treating as loan-in-use retry state",
						{
							loanGuid: params?.id,
							fileName: file?.name,
						}
					);
				}

				if (fallbackResponse) {
					loanInUseFallbackSuccessRef.current = true;
				}

				lastFallbackResponse = fallbackResponse;
			}

			return lastFallbackResponse;
		}

		const selectedFile = getCurrentRetryFile(retryContext);

		if (!selectedFile) {
			handleErrorModal(
				"DOCUMENT_UPLOAD_ERROR",
				new Error("Missing file for S3 fallback retry")
			);
			return null;
		}

		const fallbackResponse = await uploadEnhancedConditionFileToS3Fallback(
			selectedFile,
			{
				conditionId:
					retryContext?.conditionId ??
					latestRetryContextRef.current?.conditionId ??
					"",
				conditionName:
					retryContext?.conditionName ??
					latestRetryContextRef.current?.conditionName ??
					"",
				conditionType:
					retryContext?.conditionType ??
					latestRetryContextRef.current?.conditionType ??
					"",
				entityId:
					retryContext?.entityId ??
					latestRetryContextRef.current?.entityId ??
					"",
				entityType:
					retryContext?.entityType ??
					latestRetryContextRef.current?.entityType ??
					"Document",
				title:
					retryContext?.title ??
					latestRetryContextRef.current?.title ??
					selectedFile?.name ??
					"",
			}
		);

		const s3Url = extractFallbackS3Url(fallbackResponse);

		if (s3Url) {
			rememberRetryContext({
				...retryContext,
				file: selectedFile,
				originalFileName: selectedFile?.name,
				attachmentFilename: selectedFile?.name,
				attachmentSize: selectedFile?.size ?? "",
				contentType: selectedFile?.type ?? "",
				s3Url,
				fileContent: selectedFile?.content ?? "",
			});
		} else {
			supportingDocsLog(
				"[SupportingDocs] S3 fallback returned 200 without s3_url; treating as loan-in-use retry state",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
				}
			);
		}

		if (fallbackResponse) {
			loanInUseFallbackSuccessRef.current = true;
		}

		return fallbackResponse;
	}

	async function retryLoanInUseFallback(retryContext = {}) {
		return retryConflictWithS3Fallback(retryContext);
	}

	const uploadStandardFileToS3Fallback = async (
		selectedFile,
		selectedConditionIds = [],
		loanType = "Standard"
	) =>
		uploadFileWithChunking({
			file: selectedFile,
			thresholdBytes: CHUNK_SIZE_BYTES,
			chunkSizeBytes: CHUNK_SIZE_BYTES,
			uploadPart: async ({ file: uploadFile, meta }) => {
				supportingDocsLog(
					"[SupportingDocs] Calling API: standardDocumentUploadFileForS3Fallback",
					{
						loanGuid: params?.id,
						fileName: selectedFile?.name,
						fileSize: selectedFile?.size,
						meta,
					}
				);
				const formData = new FormData();
				formData.append("loan_guid", params?.id);
				formData.append("file", uploadFile, selectedFile?.name);

				const resolvedConditionIds = Array.isArray(selectedConditionIds)
					? selectedConditionIds
							.map((conditionId) => String(conditionId).trim())
							.filter(Boolean)
					: [];
				const resolvedConditionDetails = resolvedConditionIds
					.map((conditionId) => {
						const matchedCondition = conditionRows.find(
							(row) => String(row?.id ?? "").trim() === conditionId
						);

						return {
							id: conditionId,
							name: String(
								matchedCondition?.title ??
									matchedCondition?.label ??
									matchedCondition?.name ??
									conditionId
							).trim(),
							type: String(matchedCondition?.conditionType ?? "").trim(),
						};
					})
					.filter((item) => item.id);

				if (resolvedConditionIds.length > 0) {
					resolvedConditionIds.forEach((conditionId) => {
						formData.append("condition_ids", conditionId);
					});
				}
				formData.append("loan_type", loanType);
				resolvedConditionDetails.forEach(({ name, type }) => {
					formData.append("condition_name", name);
					formData.append("condition_type", type);
				});

				appendChunkMetadata(formData, meta);
				rememberRetryContext({
					file: selectedFile,
					originalFileName: selectedFile?.name,
					attachmentFilename: selectedFile?.name,
					attachmentSize: selectedFile?.size ?? "",
					contentType: selectedFile?.type ?? "",
					fileContent: selectedFile?.content ?? "",
				});

				try {
					const response = await standardDocumentUploadFileForS3Fallback({
						formData,
					}).unwrap();
					loanInUseFallbackSuccessRef.current = true;
					supportingDocsLog(
						"[SupportingDocs] API response: standardDocumentUploadFileForS3Fallback",
						{
							loanGuid: params?.id,
							fileName: selectedFile?.name,
							response,
						}
					);
					return response;
				} catch (error) {
					handleErrorModal("DOCUMENT_UPLOAD_ERROR", error);
					throw error;
				}
			},
		});

	// Two-phase upload mirroring the Enhanced flow: ask the backend for an
	// Encompass upload plan first (metadata only, fast), then either proxy a
	// single PUT through the backend or PUT multi-part chunks directly from
	// the browser to Encompass (bypassing our own server/gateway for the slow
	// transfer, which was hitting gateway timeouts on very large files).
	const uploadLoanDocumentFile = async (element) => {
		const selectedFile = getSelectedFile(element);

		supportingDocsLog(
			"[SupportingDocs] Calling API: getStandardDocumentUploadUrl",
			{
				loanGuid: params?.id,
				fileName: selectedFile?.name,
				fileSize: selectedFile?.size ?? 0,
				user_email: currentLoggedInUserEmail,
			}
		);
		const planResponse = await getStandardDocumentUploadUrl({
			loanGuid: params?.id,
			fileName: selectedFile?.name,
			fileSize: selectedFile?.size ?? 0,
			user_email: currentLoggedInUserEmail,
		}).unwrap();
		supportingDocsLog(
			"[SupportingDocs] API response: getStandardDocumentUploadUrl",
			{
				loanGuid: params?.id,
				fileName: selectedFile?.name,
				response: planResponse,
			}
		);

		const uploadData = planResponse?.data ?? {};
		rememberRetryContext({
			file: selectedFile,
			originalFileName: selectedFile?.name,
			attachmentFilename: selectedFile?.name,
			attachmentSize: selectedFile?.size ?? "",
			contentType: selectedFile?.type ?? "",
			s3Url:
				uploadData?.uploadUrl ?? uploadData?.url ?? uploadData?.s3Url ?? "",
			fileContent: selectedFile?.content ?? "",
		});

		if (uploadData?.needsS3Fallback) {
			supportingDocsLog(
				"[SupportingDocs] Condition uploadData.needsS3Fallback = true, continuing",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
				}
			);
			return uploadStandardFileToS3Fallback(selectedFile);
		}
		supportingDocsLog(
			"[SupportingDocs] Condition uploadData.needsS3Fallback = false, continuing",
			{
				loanGuid: params?.id,
				fileName: selectedFile?.name,
			}
		);

		const uploadUrl = uploadData?.multiChunkRequired
			? uploadData?.multiChunk?.commitUrl
			: uploadData?.uploadUrl || uploadData?.url;
		const uploadAuthorization =
			uploadData?.authorizationHeader ||
			uploadData?.authorization ||
			uploadData?.authHeader ||
			uploadData?.auth ||
			uploadData?.signature;

		rememberRetryContext({
			file: selectedFile,
			originalFileName: selectedFile?.name,
			attachmentFilename: selectedFile?.name,
			attachmentSize: selectedFile?.size ?? "",
			contentType: selectedFile?.type ?? "",
			s3Url: uploadUrl,
			fileContent: selectedFile?.content ?? "",
		});

		if (!uploadUrl) {
			supportingDocsLog(
				"[SupportingDocs] uploadData missing required uploadUrl, returning early",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
					uploadData,
					returnValue: null,
				}
			);
			throw new Error("DOCUMENT_UPLOAD_ERROR");
		}

		if (uploadData?.multiChunkRequired) {
			supportingDocsLog(
				"[SupportingDocs] Condition uploadData.multiChunkRequired = true, continuing",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
					uploadKey: getUploadTrackerKey(uploadData, selectedFile),
				}
			);
			const uploadKey = getUploadTrackerKey(uploadData, selectedFile);
			setDocumentTrackerUploadProgress?.(
				buildMultipartUploadProgress(selectedFile, uploadKey)
			);
			try {
				supportingDocsLog("[SupportingDocs] Calling API: uploadMultipartFile", {
					loanGuid: params?.id,
					fileName: selectedFile?.name,
					uploadUrl,
					authorizationPresent: Boolean(uploadAuthorization),
					chunkCount: uploadData?.multiChunk?.chunkList?.length ?? 0,
				});
				await uploadMultipartFile({
					file: selectedFile,
					chunkList: uploadData?.multiChunk?.chunkList,
					commitUrl: uploadUrl,
					authorization: uploadAuthorization,
				});
				supportingDocsLog(
					"[SupportingDocs] API response: uploadMultipartFile",
					{
						loanGuid: params?.id,
						fileName: selectedFile?.name,
						uploadUrl,
						status: "fulfilled",
					}
				);
			} catch (error) {
				supportingDocsLog(
					"[SupportingDocs] API response: uploadMultipartFile",
					{
						loanGuid: params?.id,
						fileName: selectedFile?.name,
						uploadUrl,
						status: "rejected",
						error,
					}
				);
				setDocumentTrackerUploadProgress?.(null);
				throw error;
			}
		} else {
			supportingDocsLog(
				"[SupportingDocs] Condition uploadData.multiChunkRequired = false, continuing",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
				}
			);
			supportingDocsLog(
				"[SupportingDocs] Calling API: attachEnhancedConditionDocumentToUploadUrl",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
					uploadUrl,
					authorizationPresent: Boolean(uploadAuthorization),
				}
			);
			try {
				await attachEnhancedConditionDocumentToUploadUrl({
					uploadUrl,
					authorization: uploadAuthorization,
					file: selectedFile,
					user_email: currentLoggedInUserEmail,
				}).unwrap();
			} catch (error) {
				if (isConflictError(error)) {
					await retryConflictWithS3Fallback({
						documentKind: "standard",
						loanType: "Standard",
						conditionId: uploadData?.conditionId,
						conditionName: uploadData?.conditionName,
						conditionType: uploadData?.conditionType,
						entityId: uploadData?.entityId,
						entityType: uploadData?.entityType,
						title: uploadData?.title ?? selectedFile?.name,
						file: selectedFile,
						uploadedBy: currentLoggedInUserEmail,
					});
					throw createRetryHandledError();
				}
				throw error;
			}
			supportingDocsLog(
				"[SupportingDocs] API response: attachEnhancedConditionDocumentToUploadUrl",
				{
					loanGuid: params?.id,
					fileName: selectedFile?.name,
					uploadUrl,
					status: "fulfilled",
				}
			);
		}

		supportingDocsLog(
			"[SupportingDocs] Returning from uploadLoanDocumentFile",
			{
				loanGuid: params?.id,
				fileName: selectedFile?.name,
				returnValue: {
					file_unique_id: uploadData?.attachmentId ?? null,
					data: "File Uploaded Successfully",
				},
			}
		);
		return {
			file_unique_id: uploadData?.attachmentId ?? null,
			data: "File Uploaded Successfully",
		};
	};

	const conditionTypeOptions = drawerData?.nextConditionsOptions ?? [];
	const normalizeConditionOption = (option) => {
		if (typeof option === "string") {
			return {
				id: option,
				title: option,
				label: option,
				name: option,
				entityName: option,
				category: "",
				internalDescription: "",
			};
		}

		const title =
			option?.title ??
			option?.label ??
			option?.name ??
			option?.entityName ??
			"";
		const id = option?.id ?? option?.entityId ?? option?.value ?? title ?? "";

		return {
			...option,
			id,
			title,
			label: title,
			name: title,
			entityName: title,
			category: option?.category ?? option?.conditionCategory ?? "",
			description:
				option?.description ??
				option?.internalDescription ??
				option?.externalDescription ??
				"",
			internalDescription:
				option?.internalDescription ?? option?.description ?? "",
		};
	};

	const selectedConditionValues = Array.isArray(selectedConditionTypes)
		? selectedConditionTypes
		: [];
	const normalizedConditionOptions = conditionTypeOptions.map(
		normalizeConditionOption
	);
	const uploadButtonText =
		attachedFiles?.length > 0
			? `Upload ${attachedFiles.length} File${
					attachedFiles.length === 1 ? "" : "s"
				}`
			: "Upload";
	const conditionRows = useMemo(
		() =>
			normalizedConditionOptions
				.map((option) => ({
					id: option.id,
					title: option.label,
					label: option.label,
					name: option.label,
					entityName: option.label,
					category: option.category,
					internalDescription: option.internalDescription,
					conditionType: option.conditionType,
					sub_status: option.sub_status,
				}))
				.filter((row) => {
					const subStatus = String(row?.sub_status ?? "")
						.trim()
						.toLowerCase();

					return !["cleared", "fulfilled", "waived"].includes(subStatus);
				}),
		[normalizedConditionOptions]
	);

	const normalizeSelectedConditionTypes = (value) =>
		(Array.isArray(value) ? value : [])
			.map((item) =>
				typeof item === "string"
					? item
					: (item?.id ?? item?.entityId ?? item?.value ?? item?.label ?? "")
			)
			.filter(Boolean);

	useEffect(() => {
		setSelectedConditionTypes(
			normalizeSelectedConditionTypes(drawerData?.selectedConditionTypes)
		);
	}, [drawerData?.selectedConditionTypes]);

	const handleFileAttach = async (file) => {
		const newFiles = Array.from(file);
		const filesWithContent = await Promise.all(
			newFiles.map(async (item) => ({
				file: item,
				title: item?.name ?? "",
				content: await fileToBase64(item),
			}))
		);

		setAttachedFiles((prev) => [...prev, ...filesWithContent]);
	};

	const deleteFile = (event, index) => {
		setAttachedFiles((prev) =>
			prev.filter((_, currentIndex) => currentIndex !== index)
		);
	};

	const handleCancel = () => {
		setAttachedFiles([]);
	};

	// Pulls the actual dynamic reason out of an error payload (Encompass's own
	// code/summary/details when present, or our backend's message) so the
	// error modal can show why an upload really failed instead of a generic
	// canned message.
	const extractErrorDetail = (data) => {
		if (!data) return undefined;
		if (typeof data === "string") return data;

		const encompassBody =
			data?.data?.data && typeof data.data.data === "object"
				? data.data.data
				: null;

		return (
			encompassBody?.details ||
			encompassBody?.summary ||
			(typeof data?.data?.data === "string" ? data.data.data : undefined) ||
			data?.data?.details ||
			data?.data?.message ||
			data?.message ||
			data?.error ||
			undefined
		);
	};

	const handleErrorModal = (errorKey, data) => {
		handleCloseProcessingModal();

		if (isConflictError(data)) {
			openUploadSuccessModalOnce(LOAN_IN_USE_FALLBACK_MESSAGE);
			setAttachedFiles([]);
			return;
		}

		const errorDetail = extractErrorDetail(data);

		if (
			data?.status === "FETCH_ERROR" ||
			data?.status === 413 ||
			data?.OriginalStatus === 413 ||
			data?.originalStatus === 413
		) {
			// Use the new CommonErrorModal for file size limit errors
			handleOpenCommonErrorModal(
				errorKey ? errorKey : "FILE_SIZE_TOO_LARGE",
				errorDetail
			);
		} else {
			// Use the new CommonErrorModal for other errors
			handleOpenCommonErrorModal(
				errorKey ? errorKey : "DOCUMENT_UPLOAD_ERROR",
				errorDetail
			);
		}
	};

	const normalizeFolderName = (value) =>
		String(value ?? "")
			.trim()
			.toLowerCase();

	const getDocumentFolderName = (document) =>
		normalizeFolderName(
			document?.title ?? document?.document_name ?? document?.name
		);

	const checkIfFolderExists = async (loanGuid, selectedDocument) => {
		try {
			supportingDocsLog(
				"[SupportingDocs] Calling API: getEnhancedConditionsDocumentsList",
				{
					loanGuid,
					selectedDocumentId: selectedDocument?.id,
					selectedDocumentTitle: selectedDocument?.title,
				}
			);
			const loanDocuments = await getEnhancedConditionsDocumentsList({
				loanGuid,
				apiRequest: enhancedConditionsDocumentsList,
				handleErrorModal,
				errorKey: "ENHANCED_CONDITION_LOAN_DOCUMENTS_UNAVAILABLE",
			});
			supportingDocsLog(
				"[SupportingDocs] API response: getEnhancedConditionsDocumentsList",
				{
					loanGuid,
					selectedDocumentId: selectedDocument?.id,
					response: loanDocuments,
				}
			);

			if (!loanDocuments) {
				supportingDocsLog(
					"[SupportingDocs] getEnhancedConditionsDocumentsList returned null/undefined, returning early",
					{
						loanGuid,
						returnValue: null,
					}
				);
				return null;
			}

			const selectedFolderName = normalizeFolderName(selectedDocument?.title);
			const matchedFolder = loanDocuments?.data?.find((doc) => {
				const existingFolderName = getDocumentFolderName(doc);

				return (
					existingFolderName === selectedFolderName ||
					existingFolderName.includes(selectedFolderName) ||
					selectedFolderName.includes(existingFolderName)
				);
			});

			if (matchedFolder) {
				supportingDocsLog(
					"[SupportingDocs] Condition matchedFolder = true, returning existing folder id",
					{
						loanGuid,
						selectedDocumentId: selectedDocument?.id,
						matchedFolderId: matchedFolder?.id,
						returnValue: matchedFolder?.id,
					}
				);
				return matchedFolder?.id;
			}

			// Create the eFolder when no existing folder matches the selected document.
			supportingDocsLog(
				"[SupportingDocs] Calling API: enhancedConditionsEFolderCreation",
				{
					loanGuid: selectedLoanId,
					selectedDocumentId: selectedDocument?.id,
					selectedDocumentTitle: selectedDocument?.title,
					description:
						selectedDocument?.description ||
						selectedDocument?.internalDescription ||
						selectedDocument?.externalDescription,
				}
			);
			const createdFolder = await enhancedConditionsEFolderCreation({
				documentName: selectedDocument?.title,
				description: selectedDocument?.title,
				loanGuid: selectedLoanId,
			}).unwrap();
			supportingDocsLog(
				"[SupportingDocs] API response: enhancedConditionsEFolderCreation",
				{
					loanGuid: selectedLoanId,
					selectedDocumentId: selectedDocument?.id,
					response: createdFolder,
				}
			);

			supportingDocsLog("[SupportingDocs] Returning from checkIfFolderExists", {
				loanGuid,
				selectedDocumentId: selectedDocument?.id,
				returnValue: createdFolder?.data?.[0]?.id ?? null,
			});
			return createdFolder?.data?.[0]?.id ?? null;
		} catch (error) {
			if (isConflictError(error)) {
				const fallbackResponse = await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId: selectedDocument?.id,
					conditionName: selectedDocument?.label ?? selectedDocument?.title,
					conditionType: selectedDocument?.conditionType,
					entityId: selectedDocument?.entityId ?? selectedDocument?.id,
					entityType: selectedDocument?.entityType ?? "Document",
					title: selectedDocument?.title ?? selectedDocument?.label,
					file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
					uploadedBy: currentLoggedInUserEmail,
				});
				if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
					showLoanInUseFallbackSuccess();
				}
				return null;
			}
			handleErrorModal("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED", error);
			handleCloseProcessingModal();
			return null;
		}
	};

	const efolderDetails = async (selectedDocument) => {
		try {
			let documentName = "standard_default_document";
			const selectedDocumentName =
				selectedDocument?.title ??
				selectedDocument?.label ??
				selectedDocument?.name ??
				selectedDocument?.entityName ??
				"";

			if (isEnhancedEnabled === true) {
				// Load the template list first so we can map the selected document to a template.
				supportingDocsLog(
					"[SupportingDocs] Calling API: enhancedConditionsTemplates",
					{
						selectedDocumentId: selectedDocument?.id,
						selectedDocumentName: selectedDocumentName,
					}
				);
				const templatesResponse = await enhancedConditionsTemplates().unwrap();
				supportingDocsLog(
					"[SupportingDocs] API response: enhancedConditionsTemplates",
					{
						selectedDocumentId: selectedDocument?.id,
						response: templatesResponse,
					}
				);

				const documentTitle =
					selectedDocument?.title ??
					selectedDocument?.label ??
					selectedDocument?.name ??
					selectedDocument?.entityName ??
					"";

				const matchedTemplate = templatesResponse?.data?.find(
					(item) =>
						documentTitle &&
						item?.title &&
						documentTitle.toLowerCase().includes(item.title.toLowerCase())
				);

				const selectedId = matchedTemplate?.id;

				if (!selectedId) {
					supportingDocsLog(
						"[SupportingDocs] selectedId missing after template matching, returning early",
						{
							selectedDocumentId: selectedDocument?.id,
							selectedDocumentName: selectedDocumentName,
							templatesResponse,
							returnValue: null,
						}
					);
					handleErrorModal(
						"ENHANCED_CONDITION_UPLOAD_FAILED",
						templatesResponse
					);
					return null;
				}

				supportingDocsLog(
					"[SupportingDocs] Calling API: enhancedConditionsTemplateDetails",
					{
						selectedId,
						selectedDocumentId: selectedDocument?.id,
					}
				);
				const templateDetails = await enhancedConditionsTemplateDetails({
					id: selectedId,
				}).unwrap();
				supportingDocsLog(
					"[SupportingDocs] API response: enhancedConditionsTemplateDetails",
					{
						selectedId,
						selectedDocumentId: selectedDocument?.id,
						response: templateDetails,
					}
				);

				const folderNameCondition =
					templateDetails?.data?.connectSettings?.documentOption;

				if (folderNameCondition === "MatchConditionName") {
					supportingDocsLog(
						"[SupportingDocs] Condition folderNameCondition === 'MatchConditionName' = true, continuing",
						{
							selectedId,
							selectedDocumentId: selectedDocument?.id,
						}
					);
					return checkIfFolderExists(params?.id, selectedDocument);
				}
				supportingDocsLog(
					"[SupportingDocs] Condition folderNameCondition === 'MatchConditionName' = false, continuing",
					{
						selectedId,
						selectedDocumentId: selectedDocument?.id,
						folderNameCondition,
					}
				);

				// Resolve the default document name and ensure the folder exists.
				supportingDocsLog(
					"[SupportingDocs] Calling API: getEnhancedConditionsEfolderDocumentsList",
					{
						selectedId,
						selectedDocumentId: selectedDocument?.id,
					}
				);
				const efolderDocuments =
					await getEnhancedConditionsEfolderDocumentsList().unwrap();
				supportingDocsLog(
					"[SupportingDocs] API response: getEnhancedConditionsEfolderDocumentsList",
					{
						selectedId,
						selectedDocumentId: selectedDocument?.id,
						response: efolderDocuments,
					}
				);
				documentName = efolderDocuments?.data?.find(
					(doc) =>
						doc?.id ===
						templateDetails?.data?.connectSettings?.documentTemplate?.entityId
				)?.name;
			}
			supportingDocsLog("[SupportingDocs] Calling API: checkIfFolderExists", {
				selectedDocumentId: selectedDocument?.id,
				selectedDocumentName,
				documentName,
				isEnhancedEnabled,
			});
			const efolderResult = await checkIfFolderExists(params?.id, {
				title: isEnhancedEnabled === true ? documentName : selectedDocumentName,
				internalDescription: `Default Document with name ${documentName}`,
			});
			supportingDocsLog("[SupportingDocs] API response: checkIfFolderExists", {
				selectedDocumentId: selectedDocument?.id,
				returnValue: efolderResult,
			});
			supportingDocsLog(
				"[SupportingDocs] efolderDetails resolved folder lookup",
				{
					selectedDocumentId: selectedDocument?.id,
					returnValue: efolderResult,
				}
			);
			return efolderResult;
		} catch (error) {
			if (isConflictError(error)) {
				await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId: selectedDocument?.id,
					conditionName: selectedDocument?.label ?? selectedDocument?.title,
					conditionType: selectedDocument?.conditionType,
					entityId: selectedDocument?.entityId ?? selectedDocument?.id,
					entityType: selectedDocument?.entityType ?? "Document",
					title: selectedDocument?.title ?? selectedDocument?.label,
					file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
					uploadedBy: currentLoggedInUserEmail,
				});
				throw createRetryHandledError();
			}
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	const buildEnhancedUploadBody = async (file, efolderId, selectedDocument) => {
		const selectedFile = getSelectedFile(file);
		const title = file?.title || selectedFile?.name || "";

		return {
			user_email: currentLoggedInUserEmail,
			title,
			file: {
				contentType:
					selectedFile?.type ||
					getMimeTypeFromFilename(title) ||
					GENERIC_FALLBACK_CONTENT_TYPE,
				name: title,
				size: selectedFile?.size ?? 0,
			},
			assignTo: {
				entityId: efolderId,
				entityType: "Document",
			},
			conditionId: selectedDocument?.id,
			conditionName: selectedDocument?.label,
		};
	};

	const getEnhancedUploadRequest = async (enhancedUploadBody, selectedFile) => {
		try {
			const uploadDocumentResponse =
				await enhancedConditionsUploadUrlAndAssignEfolder({
					loanGuid: params?.id,
					body: enhancedUploadBody,
				}).unwrap();

			const uploadData = uploadDocumentResponse?.data ?? {};

			if (uploadData?.needsS3Fallback) {
				return {
					needsS3Fallback: true,
					conditionId: uploadData?.conditionId,
					conditionName: uploadData?.conditionName,
					entityId: uploadData?.entityId,
					entityType: uploadData?.entityType,
					title: uploadData?.title,
				};
			}

			const uploadUrl = uploadData?.multiChunkRequired
				? uploadData?.multiChunk?.commitUrl
				: uploadData?.uploadUrl || uploadData?.url;
			const uploadAuthorization =
				uploadData?.authorizationHeader ||
				uploadData?.authorization ||
				uploadData?.authHeader ||
				uploadData?.auth ||
				uploadData?.signature;

			if (!uploadUrl) {
				handleErrorModal(
					"ENHANCED_CONDITION_UPLOAD_FAILED",
					uploadDocumentResponse
				);
				return null;
			}
			rememberRetryContext({
				file: enhancedUploadBody?.file,
				originalFileName: enhancedUploadBody?.file?.name,
				attachmentFilename: enhancedUploadBody?.file?.name,
				attachmentSize: enhancedUploadBody?.file?.size ?? "",
				contentType: enhancedUploadBody?.file?.contentType ?? "",
				s3Url: uploadUrl,
				conditionId: enhancedUploadBody?.conditionId,
				conditionName: enhancedUploadBody?.conditionName,
				entityId: enhancedUploadBody?.assignTo?.entityId,
				entityType: enhancedUploadBody?.assignTo?.entityType,
				title: enhancedUploadBody?.title,
			});

			return {
				uploadUrl,
				uploadAuthorization,
				attachmentId: uploadData?.attachmentId,
				multiChunkRequired: uploadData?.multiChunkRequired,
				multiChunk: uploadData?.multiChunk,
				conditionId: enhancedUploadBody?.conditionId,
				conditionName: enhancedUploadBody?.conditionName,
				entityId: enhancedUploadBody?.assignTo?.entityId,
				entityType: enhancedUploadBody?.assignTo?.entityType,
				title: enhancedUploadBody?.title,
			};
		} catch (error) {
			if (isConflictError(error)) {
				await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId: enhancedUploadBody?.conditionId,
					conditionName: enhancedUploadBody?.conditionName,
					entityId: enhancedUploadBody?.assignTo?.entityId,
					entityType: enhancedUploadBody?.assignTo?.entityType,
					title: enhancedUploadBody?.title,
					file: selectedFile,
					uploadedBy: enhancedUploadBody?.user_email,
				});
				throw createRetryHandledError();
			}
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	const uploadEnhancedConditionFileToS3Fallback = async (
		file,
		{ conditionId, conditionName, conditionType, entityId, entityType, title }
	) => {
		const ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES = 59 * 1024 * 1024; // 59MB
		const selectedFile = getSelectedFile(file);

		return uploadFileWithChunking({
			file: selectedFile,
			thresholdBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
			chunkSizeBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
			uploadPart: async ({ file: uploadFile, meta }) => {
				const formData = new FormData();
				formData.append("title", title || selectedFile?.name || "");
				formData.append("condition_id", conditionId ?? "");
				formData.append("condition_name", conditionName ?? "");
				formData.append("entity_id", entityId ?? "");
				formData.append("entity_type", entityType || "Document");
				formData.append("loan_guid", params?.id ?? "");
				formData.append("file", uploadFile, selectedFile?.name);

				if (meta?.isChunked) {
					formData.append("chunk_index", String(meta?.chunkIndex));
					formData.append("total_chunks", String(meta?.totalChunks));
					formData.append("upload_id", meta?.uploadId);
					formData.append("original_file_name", meta?.originalFileName);
					formData.append("original_file_size", String(meta?.originalFileSize));
					formData.append("is_chunked", "true");
					formData.append("is_last_chunk", String(meta?.isLastChunk));
				}
				rememberRetryContext({
					file: selectedFile,
					originalFileName: selectedFile?.name,
					attachmentFilename: selectedFile?.name,
					attachmentSize: selectedFile?.size ?? "",
					contentType: selectedFile?.type ?? "",
					fileContent: selectedFile?.content ?? "",
					conditionId,
					conditionName,
					conditionType,
					entityId,
					entityType,
					title,
				});

				try {
					const response = await enhancedLoanUploadAttachmentForS3Fallback({
						loanGuid: params?.id,
						formData,
					}).unwrap();
					loanInUseFallbackSuccessRef.current = true;
					return response;
				} catch (error) {
					if (isConflictError(error)) {
						const fallbackResponse = await retryConflictWithS3Fallback({
							documentKind: isEnhancedEnabled ? "enhanced" : "standard",
							loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
							conditionId,
							conditionName,
							conditionType,
							entityId,
							entityType,
							title,
							file: selectedFile,
							uploadedBy: currentLoggedInUserEmail,
						});
						if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
							return fallbackResponse;
						}
						throw createRetryHandledError();
					}
					handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
					return null;
				}
			},
		});
	};

	const uploadEnhancedConditionFile = async ({
		uploadUrl,
		uploadAuthorization,
		multiChunkRequired,
		multiChunk,
		uploadKey,
		file,
		conditionId,
		conditionName,
		conditionType,
		entityId,
		entityType,
		title,
	}) => {
		const selectedFile = getSelectedFile(file);
		rememberRetryContext({
			file: selectedFile,
			originalFileName: selectedFile?.name,
			attachmentFilename: selectedFile?.name,
			attachmentSize: selectedFile?.size ?? "",
			contentType: selectedFile?.type ?? "",
			s3Url: uploadUrl,
			fileContent: selectedFile?.content ?? "",
		});

		try {
			if (multiChunkRequired || Array.isArray(multiChunk?.chunkList)) {
				const trackerUploadKey =
					uploadKey ?? getUploadTrackerKey({}, selectedFile);
				setDocumentTrackerUploadProgress?.(
					buildMultipartUploadProgress(selectedFile, trackerUploadKey)
				);
				try {
					return await uploadMultipartFile({
						file: selectedFile,
						chunkList: multiChunk?.chunkList,
						commitUrl: uploadUrl,
						authorization: uploadAuthorization,
					});
				} catch (error) {
					setDocumentTrackerUploadProgress?.(null);
					throw error;
				}
			}

			const uploadResponse = await attachEnhancedConditionDocumentToUploadUrl({
				uploadUrl,
				authorization: uploadAuthorization,
				file: selectedFile,
				user_email: currentLoggedInUserEmail,
				loanGuid: params?.id,
				conditionId,
				conditionName,
				entityId,
				entityType,
				title,
				loanType: isEnhancedEnabled ? "enhanced" : "standard",
			}).unwrap();

			if (isRetryResponse(uploadResponse)) {
				setDocumentTrackerUploadProgress?.(null);
				handleCloseProcessingModal();
				showLoanInUseFallbackSuccess();
				return null;
			}

			return uploadResponse;
		} catch (error) {
			if (isConflictError(error)) {
				await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId,
					conditionName,
					conditionType,
					entityId,
					entityType,
					title,
					file: selectedFile,
					uploadedBy: currentLoggedInUserEmail,
				});
				throw createRetryHandledError();
			}
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
			return null;
		}
	};

	const mapDocumentsToConditions = (
		documents,
		efolderId,
		conditionId,
		conditionType
	) => {
		const result = [];

		const conditionMapping = {
			document_id: efolderId,
			attachment_id: efolderId,
			condition_id: conditionId,
			condition_type: conditionType ?? "underwriting",
			attachment_ids: efolderId,
		};

		result.push(conditionMapping);
		return { condition_mapping: result };
	};

	const attachEnhancedConditionDocumentRecord = async ({
		efolderId,
		selectedDocument,
		selectedDocuments,
		conditionId,
		lastUploadResponse,
	}) => {
		let attachDocumentResponse;
		const selectedDocumentConditionType =
			selectedDocument?.conditionType ??
			selectedDocument?.condition_type ??
			selectedDocuments?.conditionType ??
			selectedDocuments?.condition_type ??
			selectedDocuments?.[0]?.conditionType ??
			selectedDocuments?.[0]?.condition_type;

		try {
			if (isEnhancedEnabled) {
				attachDocumentResponse = await attachEnhancedConditionDocument({
					loanGuid: params?.id,
					data: [
						{
							entityId: efolderId,
							entityType: "Document",
						},
					],
					conditionId:
						conditionId ??
						selectedDocument?.id ??
						selectedDocument?.entityId ??
						selectedDocument?.value ??
						selectedDocument?.label ??
						"",
				}).unwrap();
			} else {
				const mappedDocuments = mapDocumentsToConditions(
					lastUploadResponse?.data ?? [],
					efolderId,
					conditionId,
					selectedDocumentConditionType
				);
				attachDocumentResponse = await attachDocumentCondition({
					loanGuid: params?.id,
					data: mappedDocuments,
				}).unwrap();
			}

			if (attachDocumentResponse === null) {
				handleErrorModal(
					"ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED",
					attachDocumentResponse
				);
				return false;
			}

			if ((attachDocumentResponse?.data ?? []).some(isRetryItem)) {
				handleCloseProcessingModal();
				showLoanInUseFallbackSuccess();
				return false;
			}

			return true;
		} catch (error) {
			if (isConflictError(error)) {
				await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId,
					conditionName: selectedDocument?.label ?? selectedDocument?.title,
					conditionType: selectedDocumentConditionType,
					entityId: efolderId,
					entityType: "Document",
					title: selectedDocument?.title ?? selectedDocument?.label,
					file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
					uploadedBy: currentLoggedInUserEmail,
				});
				throw createRetryHandledError();
			}
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED", error);
			return false;
		}
	};

	const getSelectedEnhancedDocuments = (selectedIds = selectedConditionTypes) =>
		(selectedIds || [])
			.map((selectedId) =>
				conditionTypeOptions?.find(
					(doc) =>
						String(
							doc?.id ??
								doc?.entityId ??
								doc?.value ??
								doc?.label ??
								doc?.title ??
								""
						) === String(selectedId)
				)
			)
			.filter(Boolean);

	const refreshDocumentsAfterUpload = async ({
		fileCount,
		files,
		conditionId,
		includeBrokerUploads = false,
	}) => {
		try {
			if (includeBrokerUploads) {
				await getBrokerUploads({
					loanGuid: params?.id,
					brokerUploads,
				});
			}

			await drawerData?.onUploadSuccess?.({
				fileCount,
				files,
				conditionId,
			});
		} catch (error) {
			handleErrorModal("DOCUMENT_UPLOAD_ERROR", error);
			throw error;
		}
	};

	const openUploadSuccessModalOnce = (text) => {
		if (uploadSuccessModalShownRef.current) {
			return;
		}

		uploadSuccessModalShownRef.current = true;
		handleCloseProcessingModal();
		setSuccessText(text);
		handleOpenSuccessModal();
	};

	const showLoanInUseFallbackSuccess = () => {
		loanInUseFallbackSuccessRef.current = false;
		openUploadSuccessModalOnce(LOAN_IN_USE_FALLBACK_MESSAGE);
		setAttachedFiles([]);

		// Document was saved via the S3 fallback - refresh the tracker the same
		// way the "Refresh" button does, so it shows up without a manual click.
		Promise.resolve(drawerData?.onUploadSuccess?.({})).catch(() => {});
		Promise.resolve(drawerData?.onConditionListRefresh?.()).catch(() => {});
	};

	// upload documents
	const handleSubmitDocuments = async () => {
		uploadSuccessModalShownRef.current = false;
		loanInUseFallbackSuccessRef.current = false;
		uploadedFilesThisSubmissionRef.current = new Set();
		handleCloseDrawer?.();

		let valuesArr = [
			{ id: "TPO.X4", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{
				id: "CX.LC.DOC.REC.DATE",
				value: DateTime.fromISO(DateTime.now(), {
					zone: "America/New_York",
				}).toFormat("MM-dd-yyyy hh:mm a"),
			},
		];

		const updateTpoAfterUpload = async () => {
			await updateFieldValuesTpo({
				loan_guid: params?.id,
				field_values: valuesArr,
				selectedOrganization: currentOrganization,
				userDetails,
			}).unwrap();
		};

		if (uploadConditionId) {
			const selectedDocuments = getSelectedEnhancedDocuments([
				uploadConditionId,
			]);
			supportingDocsLog(
				"[SupportingDocs] About to call runSupportingDocsEnhancedUpload",
				{
					branch: "uploadConditionId",
					uploadConditionId,
					filesCount: attachedFiles.length,
					selectedDocuments,
				}
			);

			const enhancedUploadResult = await runSupportingDocsEnhancedUpload({
				files: attachedFiles,
				processingText: `Uploading new documents for ${uploadConditionId}`,
				closeDrawer: handleCloseDrawer,
				openProcessingModal: handleOpenProcessingModal,
				setProcessingText,
				getSelectedDocuments: async () => {
					return selectedDocuments;
				},
				getDocumentContext: async (selectedDocument) => {
					const efolderId = await efolderDetails(selectedDocument);
					return efolderId ? { efolderId, selectedDocument } : null;
				},
				uploadFile: async ({ file, documentContext }) => {
					const uploadDetails = await getEnhancedUploadRequest(
						await buildEnhancedUploadBody(
							file,
							documentContext?.efolderId,
							documentContext?.selectedDocument
						),
						file
					);

					if (!uploadDetails) {
						return null;
					}

					let uploadResult;

					if (uploadDetails.needsS3Fallback) {
						uploadResult = await uploadEnhancedConditionFileToS3Fallback(file, {
							conditionId: uploadDetails.conditionId,
							conditionName: uploadDetails.conditionName,
							conditionType: uploadDetails.conditionType,
							entityId: uploadDetails.entityId,
							entityType: uploadDetails.entityType,
							title: uploadDetails.title,
						});
					} else {
						uploadResult = await uploadEnhancedConditionFile({
							uploadUrl: uploadDetails.uploadUrl,
							uploadAuthorization: uploadDetails.uploadAuthorization,
							multiChunkRequired: uploadDetails.multiChunkRequired,
							multiChunk: uploadDetails.multiChunk,
							uploadKey: uploadDetails?.attachmentId,
							conditionId: uploadDetails?.conditionId,
							conditionName: uploadDetails?.conditionName,
							conditionType: uploadDetails?.conditionType,
							entityId: uploadDetails?.entityId,
							entityType: uploadDetails?.entityType,
							title: uploadDetails?.title,
							file,
						});
					}

					if (uploadResult) {
						markFileUploaded(file);
					}

					return uploadResult;
				},
				afterDocumentUpload: async ({
					documentContext,
					selectedDocument,
					selectedDocuments,
					lastResponse,
				}) =>
					attachEnhancedConditionDocumentRecord({
						efolderId: documentContext?.efolderId,
						selectedDocument,
						selectedDocuments,
						conditionId: uploadConditionId,
						lastUploadResponse: lastResponse,
					}),
				onSuccess: async () => {
					if (loanInUseFallbackSuccessRef.current) {
						handleCloseDrawer?.();
						showLoanInUseFallbackSuccess();
						return;
					}

					try {
						supportingDocsLog(
							"[SupportingDocs] Calling API: refreshDocumentsAfterUpload",
							{
								branch: "uploadConditionId",
								fileCount: attachedFiles.length,
								files: [...attachedFiles],
								conditionId: uploadConditionId,
								includeBrokerUploads: false,
							}
						);
						await refreshDocumentsAfterUpload({
							fileCount: attachedFiles.length,
							files: [...attachedFiles],
							conditionId: uploadConditionId,
						});
						supportingDocsLog(
							"[SupportingDocs] API response: refreshDocumentsAfterUpload",
							{
								branch: "uploadConditionId",
								returnValue: undefined,
							}
						);

						supportingDocsLog(
							"[SupportingDocs] Calling API: drawerData.onRefreshDocumentsList",
							{ branch: "uploadConditionId" }
						);
						handleCloseDrawer?.();
						const refreshDocumentsListResult =
							await drawerData?.onRefreshDocumentsList?.();
						supportingDocsLog(
							"[SupportingDocs] API response: drawerData.onRefreshDocumentsList",
							{
								branch: "uploadConditionId",
								returnValue: refreshDocumentsListResult,
							}
						);
						supportingDocsLog(
							"[SupportingDocs] Calling API: drawerData.onConditionListRefresh",
							{ branch: "uploadConditionId" }
						);
						const conditionListRefreshResult =
							await drawerData?.onConditionListRefresh?.();
						supportingDocsLog(
							"[SupportingDocs] API response: drawerData.onConditionListRefresh",
							{
								branch: "uploadConditionId",
								returnValue: conditionListRefreshResult,
							}
						);
						supportingDocsLog(
							"[SupportingDocs] Calling API: waitForPendingApiRequestsToSettle",
							{ branch: "uploadConditionId" }
						);
						await waitForPendingApiRequestsToSettle(store);
						supportingDocsLog(
							"[SupportingDocs] API response: waitForPendingApiRequestsToSettle",
							{
								branch: "uploadConditionId",
								returnValue: undefined,
							}
						);

						await updateTpoAfterUpload();

						openUploadSuccessModalOnce(
							drawerData?.uploadSuccessText ??
								`File uploaded and linked to condition successfully`
						);
						setAttachedFiles([]);
					} catch (error) {
						console.error(
							"[SupportingDocs] upload success finalization failed",
							error
						);
					}
				},
				onError: async (err) => {
					setDocumentTrackerUploadProgress?.(null);
					if (loanInUseFallbackSuccessRef.current) {
						showLoanInUseFallbackSuccess();
						return;
					}
					if (isConflictError(err)) {
						const fallbackResponse = await retryLoanInUseFallback({
							documentKind: isEnhancedEnabled ? "enhanced" : "standard",
							loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
							file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
							uploadedBy: currentLoggedInUserEmail,
						});
						if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
							showLoanInUseFallbackSuccess();
						}
						return;
					}
					if (!isRetryHandledError(err)) {
						handleErrorModal("DOCUMENT_UPLOAD_ERROR", err);
					}
					handleCloseProcessingModal();
				},
			});
			supportingDocsLog(
				"[SupportingDocs] Completed runSupportingDocsEnhancedUpload",
				{
					branch: "uploadConditionId",
					uploadConditionId,
					result: enhancedUploadResult,
					continuingToNextStep: false,
				}
			);
			return;
		}

		if (selectedConditionTypes?.length > 0) {
			const selectedDocuments = getSelectedEnhancedDocuments(
				selectedConditionTypes
			);
			const selectedConditionId = selectedConditionTypes?.[0];
			supportingDocsLog(
				"[SupportingDocs] About to call runSupportingDocsEnhancedUpload",
				{
					branch: "selectedConditionTypes",
					selectedConditionId,
					selectedConditionTypes,
					filesCount: attachedFiles.length,
					selectedDocuments,
				}
			);

			const enhancedUploadResult = await runSupportingDocsEnhancedUpload({
				files: attachedFiles,
				processingText: `Uploading new documents for ${
					selectedConditionId ?? "selected condition"
				}`,
				closeDrawer: handleCloseDrawer,
				openProcessingModal: handleOpenProcessingModal,
				setProcessingText,
				getSelectedDocuments: async () => selectedDocuments,
				getDocumentContext: async (selectedDocument) => {
					const efolderId = await efolderDetails(selectedDocument);
					return efolderId ? { efolderId, selectedDocument } : null;
				},
				uploadFile: async ({ file, documentContext }) => {
					const uploadDetails = await getEnhancedUploadRequest(
						await buildEnhancedUploadBody(
							file,
							documentContext?.efolderId,
							documentContext?.selectedDocument
						),
						file
					);

					if (!uploadDetails) {
						return null;
					}

					let uploadResult;

					if (uploadDetails.needsS3Fallback) {
						uploadResult = await uploadEnhancedConditionFileToS3Fallback(file, {
							conditionId: uploadDetails.conditionId,
							conditionName: uploadDetails.conditionName,
							entityId: uploadDetails.entityId,
							entityType: uploadDetails.entityType,
							title: uploadDetails.title,
						});
					} else {
						uploadResult = await uploadEnhancedConditionFile({
							uploadUrl: uploadDetails.uploadUrl,
							uploadAuthorization: uploadDetails.uploadAuthorization,
							multiChunkRequired: uploadDetails.multiChunkRequired,
							multiChunk: uploadDetails.multiChunk,
							uploadKey: uploadDetails?.attachmentId,
							conditionId: uploadDetails?.conditionId,
							conditionName: uploadDetails?.conditionName,
							conditionType: uploadDetails?.conditionType,
							entityId: uploadDetails?.entityId,
							entityType: uploadDetails?.entityType,
							title: uploadDetails?.title,
							file,
						});
					}

					if (uploadResult) {
						markFileUploaded(file);
					}

					return uploadResult;
				},
				afterDocumentUpload: async ({
					documentContext,
					selectedDocument,
					selectedDocuments,
					lastResponse,
				}) =>
					attachEnhancedConditionDocumentRecord({
						efolderId: documentContext?.efolderId,
						selectedDocument,
						selectedDocuments,
						conditionId:
							selectedDocument?.id ??
							selectedDocument?.entityId ??
							selectedDocument?.value ??
							selectedDocument?.label ??
							selectedConditionId,
						lastUploadResponse: lastResponse,
					}),
				onSuccess: async () => {
					if (loanInUseFallbackSuccessRef.current) {
						handleCloseDrawer?.();
						showLoanInUseFallbackSuccess();
						return;
					}

					try {
						supportingDocsLog(
							"[SupportingDocs] Calling API: refreshDocumentsAfterUpload",
							{
								branch: "selectedConditionTypes",
								fileCount: attachedFiles.length,
								files: [...attachedFiles],
								conditionId: selectedConditionId,
								includeBrokerUploads: false,
							}
						);
						await refreshDocumentsAfterUpload({
							fileCount: attachedFiles.length,
							files: [...attachedFiles],
							conditionId: selectedConditionId,
						});
						supportingDocsLog(
							"[SupportingDocs] API response: refreshDocumentsAfterUpload",
							{
								branch: "selectedConditionTypes",
								returnValue: undefined,
							}
						);

						supportingDocsLog(
							"[SupportingDocs] Calling API: drawerData.onRefreshDocumentsList",
							{ branch: "selectedConditionTypes" }
						);
						handleCloseDrawer?.();
						const refreshDocumentsListResult =
							await drawerData?.onRefreshDocumentsList?.();
						supportingDocsLog(
							"[SupportingDocs] API response: drawerData.onRefreshDocumentsList",
							{
								branch: "selectedConditionTypes",
								returnValue: refreshDocumentsListResult,
							}
						);
						supportingDocsLog(
							"[SupportingDocs] Calling API: drawerData.onConditionListRefresh",
							{ branch: "selectedConditionTypes" }
						);
						const conditionListRefreshResult =
							await drawerData?.onConditionListRefresh?.();
						supportingDocsLog(
							"[SupportingDocs] API response: drawerData.onConditionListRefresh",
							{
								branch: "selectedConditionTypes",
								returnValue: conditionListRefreshResult,
							}
						);
						supportingDocsLog(
							"[SupportingDocs] Calling API: waitForPendingApiRequestsToSettle",
							{ branch: "selectedConditionTypes" }
						);
						await waitForPendingApiRequestsToSettle(store);
						supportingDocsLog(
							"[SupportingDocs] API response: waitForPendingApiRequestsToSettle",
							{
								branch: "selectedConditionTypes",
								returnValue: undefined,
							}
						);

						await updateTpoAfterUpload();

						openUploadSuccessModalOnce(
							drawerData?.uploadSuccessText ??
								`File uploaded and linked to condition successfully`
						);
						setAttachedFiles([]);
					} catch (error) {
						console.error(
							"[SupportingDocs] upload success finalization failed",
							error
						);
					}
				},
				onError: async (err) => {
					setDocumentTrackerUploadProgress?.(null);
					if (loanInUseFallbackSuccessRef.current) {
						showLoanInUseFallbackSuccess();
						return;
					}
					if (isConflictError(err)) {
						const fallbackResponse = await retryLoanInUseFallback({
							documentKind: isEnhancedEnabled ? "enhanced" : "standard",
							loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
							file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
							uploadedBy: currentLoggedInUserEmail,
						});
						if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
							showLoanInUseFallbackSuccess();
						}
						return;
					}
					if (!isRetryHandledError(err)) {
						handleErrorModal("DOCUMENT_UPLOAD_ERROR", err);
					}
					handleCloseProcessingModal();
				},
			});
			supportingDocsLog(
				"[SupportingDocs] Completed runSupportingDocsEnhancedUpload",
				{
					branch: "selectedConditionTypes",
					selectedConditionId,
					selectedConditionTypes,
					result: enhancedUploadResult,
					continuingToNextStep: false,
				}
			);
			return;
		}

		const standardUploadResult = await runSupportingDocsStandardUpload({
			files: attachedFiles,
			processingText: "Uploading new documents",
			closeDrawer: handleCloseDrawer,
			openProcessingModal: handleOpenProcessingModal,
			setProcessingText,
			buildBeforeUpload: async () => {
				try {
					await updateFieldValuesTpo({
						loan_guid: params?.id,
						field_values: valuesArr,
						selectedOrganization: currentOrganization,
						userDetails,
					}).unwrap();
				} catch (error) {
					if (isConflictError(error)) {
						await retryConflictWithS3Fallback({
							documentKind: isEnhancedEnabled ? "enhanced" : "standard",
							loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
							file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
							uploadedBy: currentLoggedInUserEmail,
						});
						throw createRetryHandledError();
					}

					throw error;
				}
			},
			uploadFile: async (file) => {
				const response = await uploadLoanDocumentFile(file);
				if (response) {
					markFileUploaded(file);
				}
				return response;
			},
			afterUpload: async (uploadDocumentResponse) => {
				if (loanInUseFallbackSuccessRef.current) {
					handleCloseDrawer?.();
					showLoanInUseFallbackSuccess();
					return;
				}

				dispatch(setLoanDocsReducer(uploadDocumentResponse));

				try {
					supportingDocsLog(
						"[SupportingDocs] Calling API: refreshDocumentsAfterUpload",
						{
							branch: "standardUpload",
							fileCount: attachedFiles.length,
							files: [...attachedFiles],
							conditionId: uploadConditionId,
							includeBrokerUploads: true,
						}
					);
					await refreshDocumentsAfterUpload({
						fileCount: attachedFiles.length,
						files: [...attachedFiles],
						conditionId: uploadConditionId,
						includeBrokerUploads: true,
					});
					supportingDocsLog(
						"[SupportingDocs] API response: refreshDocumentsAfterUpload",
						{
							branch: "standardUpload",
							returnValue: undefined,
						}
					);

					supportingDocsLog(
						"[SupportingDocs] Calling API: drawerData.onRefreshDocumentsList",
						{
							branch: "standardUpload",
						}
					);
					handleCloseDrawer?.();
					await drawerData?.onRefreshDocumentsList?.();
					supportingDocsLog(
						"[SupportingDocs] API response: drawerData.onRefreshDocumentsList",
						{
							branch: "standardUpload",
							returnValue: undefined,
						}
					);
					supportingDocsLog(
						"[SupportingDocs] Calling API: drawerData.onConditionListRefresh",
						{
							branch: "standardUpload",
						}
					);
					await drawerData?.onConditionListRefresh?.();
					supportingDocsLog(
						"[SupportingDocs] API response: drawerData.onConditionListRefresh",
						{
							branch: "standardUpload",
							returnValue: undefined,
						}
					);
					supportingDocsLog(
						"[SupportingDocs] Calling API: waitForPendingApiRequestsToSettle",
						{
							branch: "standardUpload",
						}
					);
					await waitForPendingApiRequestsToSettle(store);
					supportingDocsLog(
						"[SupportingDocs] API response: waitForPendingApiRequestsToSettle",
						{
							branch: "standardUpload",
							returnValue: undefined,
						}
					);

					openUploadSuccessModalOnce(
						drawerData?.uploadSuccessText ??
							`${attachedFiles.length} file${
								attachedFiles.length === 1 ? "" : "s"
							} uploaded`
					);
					setAttachedFiles([]);
				} catch (error) {
					console.error(
						"[SupportingDocs] upload success finalization failed",
						error
					);
				}
			},
			onError: async (err) => {
				setDocumentTrackerUploadProgress?.(null);
				if (loanInUseFallbackSuccessRef.current) {
					showLoanInUseFallbackSuccess();
					return;
				}
				if (isConflictError(err)) {
					const fallbackResponse = await retryConflictWithS3Fallback({
						documentKind: isEnhancedEnabled ? "enhanced" : "standard",
						loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
						file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
						uploadedBy: currentLoggedInUserEmail,
					});
					if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
						showLoanInUseFallbackSuccess();
					}
					return;
				}
				if (!isRetryHandledError(err)) {
					handleErrorModal("DOCUMENT_UPLOAD_ERROR", err);
				}
			},
		});

		if (standardUploadResult) {
			try {
				await updateTpoAfterUpload();
			} catch (error) {
				if (isConflictError(error)) {
					const fallbackResponse = await retryConflictWithS3Fallback({
						documentKind: isEnhancedEnabled ? "enhanced" : "standard",
						loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
						file: latestRetryContextRef.current?.file ?? attachedFiles?.[0],
						uploadedBy: currentLoggedInUserEmail,
					});
					if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
						showLoanInUseFallbackSuccess();
					}
					return;
				}

				if (!isRetryHandledError(error)) {
					handleErrorModal("DOCUMENT_UPLOAD_ERROR", error);
				}
			}
		}
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.drawerHeaderContainer}
			>
				<Heading3
					text={uploadHeaderText}
					extraClass={styles.headerText}
					fontType="semibold"
				/>
				<SecondaryButton
					startIcon={<CloseIcon />}
					text="Close"
					onClick={handleCloseDrawer}
				/>
			</Stack>
			<Stack direction="column" className={styles.drawerBodyContainer}>
				{!hideConditionSelection && (
					<Stack direction="column" className={styles.conditionsContainer}>
						<Heading3
							text="Select Conditions"
							extraClass={styles.labelText}
							fontType="semibold"
						/>
						<Box className={styles.conditionTableContainer}>
							<CustomTable
								tableType="document-tracker-conditions"
								squaredBorder={true}
								maxHeight={true}
								rowData={conditionRows}
								setSortedData={() => {}}
								selectedRow={selectedConditionValues}
								setSelectedRow={setSelectedConditionTypes}
							/>
						</Box>
					</Stack>
				)}

				<>
					<Heading3
						text="Upload Documentation:"
						extraClass={styles.labelText}
						fontType="semibold"
					/>

					<SupportingDocs
						fileCount={attachedFiles?.length}
						fileTypes={fileTypes}
						text="or drag to upload supporting files"
						handleFileAttach={handleFileAttach}
						multiple={true}
						onUnsupportedFileType={() =>
							handleOpenCommonErrorModal("CONDITION_FILE_TYPE_NOT_SUPPORTED")
						}
					/>
				</>
			</Stack>

			<>
				{attachedFiles !== undefined && attachedFiles?.length > 0 && (
					<Stack direction="column" className={styles.uploadedFileContainer}>
						{attachedFiles?.map((item, index) => {
							const selectedFile = getSelectedFile(item);

							return (
								<Stack
									direction="row"
									key={index}
									className={styles.fileListContainer}
								>
									<Box className={styles.fileIconContainer}>
										<UploadFileIcon className={styles.fileIcon} />
									</Box>
									<Stack
										direction="column"
										className={styles.fileNameContainer}
									>
										<Body2 text={selectedFile?.name} />
										<Body3 text={formatBytes(selectedFile?.size)} />
									</Stack>

									<Box
										sx={{ cursor: "pointer" }}
										onClick={(event) => deleteFile(event, index)}
										className={styles.deleteIconContainer}
									>
										<CloseIcon className={styles.deleteIcon} />
									</Box>
								</Stack>
							);
						})}
					</Stack>
				)}

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.drawerFooterContainer}
				>
					<SecondaryButton
						text="Reset"
						extraClass={styles.cancelBtn}
						extraTextClass={styles.cancelText}
						onClick={handleCancel}
						disabled={attachedFiles?.length === 0}
					/>
					<PrimaryButton
						text={uploadButtonText}
						onClick={handleSubmitDocuments}
						disabled={attachedFiles?.length === 0}
					/>
				</Stack>
			</>
		</Stack>
	);
};

export default DocumentsTrackerDrawer;
